import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { Shape } from '@eventflow/shared-types';
import { Ban, CheckCircle2, XCircle, EyeOff, Lock, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { selectSelectedShape, selectSelectedShapeIds, selectShapes } from '../mapEditorSelectors';
import { deleteShape, deleteShapes, updateShape, updateShapesBatch } from '../mapEditorSlice';

export default function ShapeProperties() {
  const dispatch = useAppDispatch();
  const selectedShape = useAppSelector(selectSelectedShape);
  const selectedShapeIds = useAppSelector(selectSelectedShapeIds);
  const allShapes = useAppSelector(selectShapes);

  const [seatIdentifier, setSeatIdentifier] = useState('');
  const [price, setPrice] = useState('');
  const [color, setColor] = useState('#22d3ee');
  const [blocked, setBlocked] = useState(false);
  const [tableId, setTableId] = useState('');
  const [tableLabel, setTableLabel] = useState('');
  const [capacity, setCapacity] = useState('12');
  const [hidden, setHidden] = useState(false);
  const [maskFill, setMaskFill] = useState('#ffffff');
  const [maskOpacity, setMaskOpacity] = useState('1');
  const [maskMargin, setMaskMargin] = useState('8');
  const [overlayFill, setOverlayFill] = useState('#ffffff');
  const [overlayOpacity, setOverlayOpacity] = useState('1');
  const [zIndex, setZIndex] = useState('100');
  const [locked, setLocked] = useState(false);
  const isMulti = (selectedShapeIds?.length || 0) > 1;

  // Actualizar valores cuando cambia la selección
  useEffect(() => {
    if (selectedShape) {
      setSeatIdentifier(selectedShape.seatIdentifier || '');
      setPrice(String(selectedShape.price || 0));
      // Si está bloqueado, mostrar rojo en el input de color, pero guardar el original
      // Si no está bloqueado, usar el fill actual o el original guardado
      if (selectedShape.blocked) {
        setColor('#ef4444'); // Mostrar rojo en el input cuando está bloqueado
      } else {
        const originalFill = (selectedShape as any).originalFill;
        const currentFill = selectedShape.fill || '#22d3ee';
        // Usar el fill actual si no es rojo, o el original guardado, o el por defecto
        setColor(
          originalFill && originalFill !== '#ef4444'
            ? originalFill
            : currentFill !== '#ef4444'
              ? currentFill
              : '#22d3ee'
        );
      }
      setBlocked(Boolean(selectedShape.blocked));
      setTableId((selectedShape as any).tableId || '');
      setTableLabel((selectedShape as any).tableLabel || '');
      setCapacity(String((selectedShape as any).capacity ?? 12));
      setHidden(Boolean(selectedShape.hidden));
      setMaskFill(((selectedShape as any).maskFill as string) || '#ffffff');
      setMaskOpacity(String((selectedShape as any).maskOpacity ?? 1));
      setMaskMargin(String((selectedShape as any).maskMargin ?? 8));
      setOverlayFill(((selectedShape as any).overlayFill as string) || '#ffffff');
      setOverlayOpacity(String((selectedShape as any).overlayOpacity ?? 1));
      setZIndex(String((selectedShape as any).zIndex ?? 100));
      setLocked(Boolean((selectedShape as any).locked));
    }
  }, [selectedShape]);

  // Guardar cambios en el shape
  const handleUpdate = (updates: Partial<Shape>) => {
    if (!selectedShape) return;

    dispatch(
      updateShape({
        ...selectedShape,
        ...updates,
      })
    );
  };

  const handleTableLabelChange = (value: string) => {
    setTableLabel(value);
    handleUpdate({ tableLabel: value } as any);
  };

  const handleCapacityChange = (value: string) => {
    setCapacity(value);
    const n = Number.parseInt(value, 10);
    if (!isNaN(n) && n > 0) {
      handleUpdate({ capacity: n } as any);
    }
  };

  const handlePriceChange = (value: string) => {
    setPrice(value);
    const numericPrice = Number.parseFloat(value);
    if (!isNaN(numericPrice) && numericPrice >= 0) {
      handleUpdate({ price: numericPrice });
    }
  };

  const handleColorChange = (value: string) => {
    setColor(value);
    handleUpdate({ fill: value });
  };

  const handleBlockedChange = (isBlocked: boolean) => {
    if (!selectedShape) return;

    setBlocked(isBlocked);
    // Si se bloquea, guardar color original y poner rojo. Si se desbloquea, restaurar color original
    if (isBlocked) {
      // Guardar el color actual como original (solo si no es rojo, porque podría venir ya bloqueado)
      const currentFill = selectedShape.fill || '#22d3ee';
      const existingOriginalFill = (selectedShape as any).originalFill;

      // Si ya tiene originalFill guardado, usarlo. Si no, guardar el fill actual (si no es rojo)
      const originalFill = existingOriginalFill
        ? existingOriginalFill
        : currentFill !== '#ef4444'
          ? currentFill
          : '#22d3ee';

      handleUpdate({
        blocked: true,
        fill: '#ef4444', // Rojo para bloqueado
        originalFill: originalFill, // Guardar color original (nunca rojo)
      } as any);
    } else {
      // Restaurar color original o color por defecto
      const originalFill = (selectedShape as any).originalFill;
      // Si hay originalFill guardado y no es rojo, usarlo. Si no, usar cyan por defecto
      const restoredColor = originalFill && originalFill !== '#ef4444' ? originalFill : '#22d3ee';

      setColor(restoredColor);
      handleUpdate({
        blocked: false,
        fill: restoredColor, // Restaurar color original
        originalFill: undefined, // Limpiar el color original guardado
      } as any);
    }
  };

  const handleHiddenChange = (value: boolean) => {
    setHidden(value);
    handleUpdate({ hidden: value });
  };

  const handleDelete = () => {
    if (!selectedShape && !isMulti) return;
    if (isMulti) {
      if (confirm(`¿Eliminar ${selectedShapeIds.length} mesa(s) seleccionadas?`)) {
        dispatch(deleteShapes(selectedShapeIds));
      }
    } else if (selectedShape) {
      if (confirm(`¿Eliminar asiento "${selectedShape.seatIdentifier}"?`)) {
        dispatch(deleteShape(selectedShape.id));
      }
    }
  };

  // ----- Controles de MULTIEDICIÓN -----
  const [multiPrice, setMultiPrice] = useState('');
  const [multiColor, setMultiColor] = useState('#22d3ee');
  const [multiWidth, setMultiWidth] = useState('');
  const [multiHeight, setMultiHeight] = useState('');
  const [multiRadius, setMultiRadius] = useState('');
  const [multiScale, setMultiScale] = useState('100');

  const applyMultiPrice = () => {
    const value = Number(multiPrice);
    if (Number.isNaN(value) || value <= 0) return;
    const targetIds = selectedShapeIds.filter((id) => {
      const s = allShapes.find((sh) => sh.id === id) as any;
      return s && s.category !== 'overlay';
    });
    if (targetIds.length > 0) {
      dispatch(updateShapesBatch({ ids: targetIds, updates: { price: value } as any }));
    }
  };

  const applyMultiColor = () => {
    const color = multiColor;
    if (!color) return;
    dispatch(
      updateShapesBatch({
        ids: selectedShapeIds,
        updates: { fill: color, overlayFill: color } as any,
      })
    );
  };

  const applyMultiRectSize = () => {
    const width = Number(multiWidth);
    const height = Number(multiHeight);
    if ((Number.isNaN(width) || width <= 0) && (Number.isNaN(height) || height <= 0)) return;
    const targetIds = selectedShapeIds.filter((id) => {
      const s = allShapes.find((sh) => sh.id === id) as any;
      return s && s.category !== 'overlay' && s.type === 'rect';
    });
    if (targetIds.length === 0) return;
    const updates: any = {};
    if (!Number.isNaN(width) && width > 0) updates.width = width;
    if (!Number.isNaN(height) && height > 0) updates.height = height;
    dispatch(updateShapesBatch({ ids: targetIds, updates }));
  };

  const applyMultiCircleRadius = () => {
    const radius = Number(multiRadius);
    if (Number.isNaN(radius) || radius <= 0) return;
    const targetIds = selectedShapeIds.filter((id) => {
      const s = allShapes.find((sh) => sh.id === id) as any;
      return s && s.category !== 'overlay' && s.type === 'circle';
    });
    if (targetIds.length === 0) return;
    dispatch(updateShapesBatch({ ids: targetIds, updates: { radius } as any }));
  };

  const applyMultiScale = () => {
    const scalePct = Number(multiScale);
    if (Number.isNaN(scalePct) || scalePct <= 0) return;
    const factor = scalePct / 100;
    const selected = allShapes.filter((sh) => selectedShapeIds.includes(sh.id));
    selected.forEach((s: any) => {
      if (s.category === 'overlay') return;
      if (s.type === 'rect') {
        const newWidth = Math.max(1, Math.round((s.width || 0) * factor));
        const newHeight = Math.max(1, Math.round((s.height || 0) * factor));
        dispatch(updateShape({ ...(s as any), width: newWidth, height: newHeight } as any));
      } else if (s.type === 'circle') {
        const newRadius = Math.max(1, Math.round((s.radius || 0) * factor));
        dispatch(updateShape({ ...(s as any), radius: newRadius } as any));
      } else if (s.type === 'polygon') {
        const pts: number[] = (s.points || []) as number[];
        if (pts.length >= 6) {
          // Calcular centroide
          const xs = pts.filter((_, i) => i % 2 === 0);
          const ys = pts.filter((_, i) => i % 2 === 1);
          const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
          const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
          const scaled = pts.map((v, i) => {
            const isX = i % 2 === 0;
            const coord = v - (isX ? cx : cy);
            return (isX ? cx : cy) + coord * factor;
          });
          dispatch(updateShape({ ...(s as any), points: scaled } as any));
        }
      }
    });
  };

  const setMultiBlocked = (value: boolean) => {
    const targetShapes = allShapes.filter((sh) => {
      const s: any = sh as any;
      return selectedShapeIds.includes(sh.id) && s.category !== 'overlay';
    });
    if (targetShapes.length === 0) return;
    if (value) {
      // Bloquear: guardar color original y poner rojo
      targetShapes.forEach((s) => {
        const anyS: any = s as any;
        const currentFill = anyS.fill || '#22d3ee';
        const existingOriginalFill = anyS.originalFill;
        const originalFill = existingOriginalFill
          ? existingOriginalFill
          : currentFill !== '#ef4444'
            ? currentFill
            : '#22d3ee';
        dispatch(
          updateShape({ ...(s as any), blocked: true, fill: '#ef4444', originalFill } as any)
        );
      });
    } else {
      // Desbloquear: restaurar color original
      targetShapes.forEach((s) => {
        const anyS: any = s as any;
        const originalFill = anyS.originalFill;
        const restoredColor = originalFill && originalFill !== '#ef4444' ? originalFill : '#22d3ee';
        dispatch(
          updateShape({
            ...(s as any),
            blocked: false,
            fill: restoredColor,
            originalFill: undefined,
          } as any)
        );
      });
    }
  };

  const setMultiHidden = (value: boolean) => {
    dispatch(updateShapesBatch({ ids: selectedShapeIds, updates: { hidden: value } as any }));
  };

  if (!selectedShape && !isMulti) {
    // Oculto en móvil (portrait y landscape), visible solo en desktop (md+)
    return (
      <div className="hidden md:block w-64 lg:w-80 bg-white border-l border-gray-200 p-4 lg:p-6">
        <div className="text-center text-gray-500 py-4 lg:py-8">
          <p className="text-sm lg:text-lg font-medium mb-1 lg:mb-2">Sin selección</p>
          <p className="text-xs lg:text-sm">Selecciona una mesa para editar</p>
        </div>
      </div>
    );
  }

  const isOverlay = Boolean((selectedShape as any)?.category === 'overlay');
  // Asegurar null-safety tras la salida temprana: alias no nulo para uso en UI
  const shape = selectedShape as Shape;

  if (isMulti) {
    const selectedForType = allShapes.filter((sh) => selectedShapeIds.includes(sh.id)) as any[];
    const hasRect = selectedForType.some((s) => s && s.category !== 'overlay' && s.type === 'rect');
    const hasCircle = selectedForType.some(
      (s) => s && s.category !== 'overlay' && s.type === 'circle'
    );
    const hasPolygon = selectedForType.some(
      (s) => s && s.category !== 'overlay' && s.type === 'polygon'
    );
    const multiTypeLabel =
      hasRect && !hasCircle && !hasPolygon
        ? 'Rectángulo'
        : !hasRect && hasCircle && !hasPolygon
          ? 'Círculo'
          : !hasRect && !hasCircle && hasPolygon
            ? 'Polígono'
            : 'multiselección';
    return (
      <div className="fixed md:relative right-0 top-[120px] bottom-0 md:top-auto md:bottom-auto w-64 md:w-64 lg:w-80 bg-white border-l border-gray-200 p-3 md:p-4 lg:p-6 overflow-y-auto md:max-h-none z-40 shadow-lg md:shadow-none">
        <h3 className="text-sm md:text-base lg:text-lg font-bold text-gray-900 mb-2 md:mb-4">
          Multiedición ({selectedShapeIds.length})
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio ($) para mesas
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={multiPrice}
                onChange={(e) => setMultiPrice(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                className="input-field"
                placeholder="Ej. 150"
              />
              <button className="btn-secondary" onClick={applyMultiPrice}>
                Aplicar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Solo afecta mesas, no overlays.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={multiColor}
                onChange={(e) => setMultiColor(e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={multiColor}
                onChange={(e) => setMultiColor(e.target.value)}
                className="input-field flex-1 font-mono text-sm"
                placeholder="#22d3ee"
              />
              <button className="btn-secondary" onClick={applyMultiColor}>
                Aplicar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Mesas: fill, Overlays: overlayFill.</p>
          </div>

          {/* Tamaño: aplicar a tipos específicos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tamaño ({multiTypeLabel})
            </label>
            <div className="space-y-3">
              {hasRect && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Rectángulos</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={multiWidth}
                      onChange={(e) => setMultiWidth(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input-field"
                      placeholder="Ancho (px)"
                    />
                    <input
                      type="number"
                      min={1}
                      value={multiHeight}
                      onChange={(e) => setMultiHeight(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input-field"
                      placeholder="Alto (px)"
                    />
                    <button className="btn-secondary" onClick={applyMultiRectSize}>
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
              {hasCircle && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Círculos</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={multiRadius}
                      onChange={(e) => setMultiRadius(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input-field"
                      placeholder="Radio (px)"
                    />
                    <button className="btn-secondary" onClick={applyMultiCircleRadius}>
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
              {(hasPolygon || (hasRect && hasCircle) || (hasPolygon && (hasRect || hasCircle))) && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Escala general</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={multiScale}
                      onChange={(e) => setMultiScale(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input-field"
                      placeholder="100 (%)"
                    />
                    <button className="btn-secondary" onClick={applyMultiScale}>
                      Aplicar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Polígonos se escalan respecto a su centroide.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={() => setMultiBlocked(true)}>
              Bloquear
            </button>
            <button className="btn-secondary" onClick={() => setMultiBlocked(false)}>
              Desbloquear
            </button>
            <span className="text-xs text-gray-500">(solo mesas)</span>
          </div>

          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={() => setMultiHidden(true)}>
              Ocultar
            </button>
            <button className="btn-secondary" onClick={() => setMultiHidden(false)}>
              Mostrar
            </button>
          </div>

          <div>
            <button className="btn-danger w-full flex items-center justify-center gap-2" onClick={handleDelete}>
              <Trash2 className="w-4 h-4" />
              Eliminar seleccionadas
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed md:relative right-0 top-[120px] bottom-0 md:top-auto md:bottom-auto w-64 md:w-64 lg:w-80 bg-white border-l border-gray-200 p-3 md:p-4 lg:p-6 overflow-y-auto md:max-h-none z-10 shadow-lg md:shadow-none">
      <h3 className="text-sm md:text-base lg:text-lg font-bold text-gray-900 mb-2 md:mb-4">
        {isOverlay ? 'Overlay' : 'Mesa'}
      </h3>

      <div className="space-y-3 md:space-y-4">
        {!isOverlay && (
          <div>
            <label htmlFor="tableId" className="block text-sm font-medium text-gray-700 mb-1">
              ID de Mesa (tableId){' '}
              <span className="text-xs text-gray-400 font-normal">(Auto-generado)</span>
            </label>
            <input
              id="tableId"
              type="text"
              value={tableId}
              disabled={true}
              readOnly={true}
              className="input-field bg-gray-50 cursor-not-allowed text-gray-600"
              placeholder="MESA-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Este ID se genera automáticamente y no se puede editar. Formato: MESA-{'{'}N{'}'}
            </p>
          </div>
        )}

        {!isOverlay && (
          <div>
            <label htmlFor="tableLabel" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre visible de la Mesa
            </label>
            <input
              id="tableLabel"
              type="text"
              value={tableLabel}
              onChange={(e) => handleTableLabelChange(e.target.value)}
              className="input-field"
              placeholder="Ej: Mesa M1"
            />
          </div>
        )}

        {!isOverlay && (
          <div>
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
              Capacidad (número de asientos)
            </label>
            <input
              id="capacity"
              type="number"
              min="1"
              step="1"
              value={capacity}
              onChange={(e) => handleCapacityChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field"
              placeholder="12"
            />
          </div>
        )}

        {/* Identificador */}
        {!isOverlay && (
          <div>
            <label htmlFor="seatId" className="block text-sm font-medium text-gray-700 mb-1">
              Identificador de la Mesa{' '}
              <span className="text-xs text-gray-400 font-normal">(Auto-generado)</span>
            </label>
            <input
              id="seatId"
              type="text"
              value={seatIdentifier}
              disabled={true}
              readOnly={true}
              className="input-field bg-gray-50 cursor-not-allowed text-gray-600"
              placeholder="MESA-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Este ID se genera automáticamente y no se puede editar. Formato: MESA-{'{'}N{'}'}
            </p>
          </div>
        )}

        {/* Precio */}
        {!isOverlay && (
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Precio ($)
            </label>
            <input
              id="price"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => handlePriceChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Precio por asiento en tu moneda local</p>
          </div>
        )}

        {/* Estado de bloqueo (mesas) */}
        {!isOverlay && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado de la Mesa
            </label>
            <div className="flex items-center">
              <input
                id="blocked"
                type="checkbox"
                checked={blocked}
                onChange={(e) => handleBlockedChange(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="blocked" className="ml-2 block text-sm text-gray-900 flex items-center gap-1">
                <Ban className="w-4 h-4" />
                Bloquear mesa
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              {blocked ? (
                <>
                  <XCircle className="w-3 h-3 text-red-500" />
                  Esta mesa NO estará disponible para la venta (color rojo)
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  Esta mesa estará disponible para la venta
                </>
              )}
            </p>
          </div>
        )}

        {/* Visibilidad pública (mesas) */}
        {!isOverlay && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visibilidad en mapa público
            </label>
            <div className="flex items-center">
              <input
                id="hidden"
                type="checkbox"
                checked={hidden}
                onChange={(e) => handleHiddenChange(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="hidden" className="ml-2 block text-sm text-gray-900 flex items-center gap-1">
                <EyeOff className="w-4 h-4" />
                Ocultar en la vista pública
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Cuando está oculto, la mesa seguirá visible en el editor con un estilo tenue, pero no
              se mostrará ni se podrá reservar en la vista pública.
            </p>
          </div>
        )}

        {/* Color (solo mesas y no bloqueado) */}
        {!isOverlay && !blocked && (
          <div>
            <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex items-center gap-2">
              <input
                id="color"
                type="color"
                value={color}
                onChange={(e) => handleColorChange(e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => handleColorChange(e.target.value)}
                className="input-field flex-1 font-mono text-sm"
                placeholder="#22d3ee"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Color de visualización en el mapa (puedes agrupar por categoría)
            </p>
          </div>
        )}

        {/* Controles de máscara (mesas ocultas) */}
        {!isOverlay && (
          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Máscara en vista pública
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Color</label>
                <input
                  type="color"
                  value={maskFill}
                  onChange={(e) => {
                    setMaskFill(e.target.value);
                    handleUpdate({ maskFill: e.target.value } as any);
                  }}
                  className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Opacidad</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={maskOpacity}
                  onChange={(e) => {
                    setMaskOpacity(e.target.value);
                    const v = Number.parseFloat(e.target.value);
                    if (!isNaN(v)) handleUpdate({ maskOpacity: v } as any);
                  }}
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Margen (px)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={maskMargin}
                  onChange={(e) => {
                    setMaskMargin(e.target.value);
                    const v = Number.parseInt(e.target.value, 10);
                    if (!isNaN(v)) handleUpdate({ maskMargin: v } as any);
                  }}
                  className="input-field"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ajusta cómo se oculta esta mesa en la vista pública.
            </p>
          </div>
        )}

        {/* Controles de overlay */}
        {isOverlay && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Apariencia del Overlay
            </label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={overlayFill}
                  onChange={(e) => {
                    setOverlayFill(e.target.value);
                    handleUpdate({ overlayFill: e.target.value, fill: e.target.value } as any);
                  }}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={overlayFill}
                  onChange={(e) => {
                    setOverlayFill(e.target.value);
                    handleUpdate({ overlayFill: e.target.value, fill: e.target.value } as any);
                  }}
                  className="input-field flex-1 font-mono text-sm"
                  placeholder="#ffffff"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Opacidad</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={overlayOpacity}
                  onChange={(e) => {
                    setOverlayOpacity(e.target.value);
                    const v = Number.parseFloat(e.target.value);
                    if (!isNaN(v)) handleUpdate({ overlayOpacity: v } as any);
                  }}
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Orden (z-index)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={zIndex}
                  onChange={(e) => {
                    setZIndex(e.target.value);
                    const v = Number.parseInt(e.target.value, 10);
                    if (!isNaN(v)) handleUpdate({ zIndex: v } as any);
                  }}
                  className="input-field"
                />
              </div>
              <div className="flex items-center">
                <input
                  id="locked"
                  type="checkbox"
                  checked={locked}
                  onChange={(e) => {
                    setLocked(e.target.checked);
                    handleUpdate({ locked: e.target.checked } as any);
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="locked" className="ml-2 block text-sm text-gray-900 flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  Bloquear overlay
                </label>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Los overlays no generan asientos y solo sirven para ocultar áreas.
            </p>
          </div>
        )}
        {/* Tipo de shape (solo lectura) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Forma</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
            {shape.type === 'circle' && 'Círculo'}
            {shape.type === 'rect' && 'Rectángulo'}
            {shape.type === 'polygon' && 'Polígono'}
          </div>
        </div>

        {/* Información adicional */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Información</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <p>
              <span className="font-medium">Posición:</span> ({shape.x?.toFixed(0)},{' '}
              {shape.y?.toFixed(0)})
            </p>
            {shape.type === 'circle' && (
              <p>
                <span className="font-medium">Radio:</span> {shape.radius?.toFixed(0)}px
              </p>
            )}
            {shape.type === 'rect' && (
              <p>
                <span className="font-medium">Dimensiones:</span> {shape.width?.toFixed(0)} x{' '}
                {shape.height?.toFixed(0)}px
              </p>
            )}
            {shape.rotation && (
              <p>
                <span className="font-medium">Rotación:</span> {shape.rotation.toFixed(1)}°
              </p>
            )}
          </div>
        </div>

        {/* Botón de eliminar */}
        <div className="pt-4">
          <button onClick={handleDelete} className="btn-danger w-full flex items-center justify-center gap-2">
            <Trash2 className="w-4 h-4" />
            Eliminar Asiento
          </button>
        </div>
      </div>
    </div>
  );
}
